import React, { useRef, useEffect, useMemo, useState } from 'react';
import { ETHERSCAN, PCAP } from './config';
import ForceGraph2D from 'react-force-graph-2d';
import ForceGraph3D from 'react-force-graph-3d';
import * as d3 from 'd3-force';
import SpriteText from 'three-spritetext';
import { SphereGeometry, TorusGeometry, Mesh, Color, DirectionalLight, MeshPhongMaterial, AmbientLight, Sprite, TextureLoader, SpriteMaterial, Texture, VideoTexture, LinearFilter, Group, MeshBasicMaterial, ShapeGeometry, LineBasicMaterial, BufferGeometry, Vector3 } from 'three';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';

// Track last node click to approximate double-clicks when the underlying
// event does not reliably expose click counts.
let lastClickNodeId = null;
let lastClickTime = 0;

// Create the alpha map (only once)
const alphaMap = createCircularAlphaMap(128); // Adjust size as needed
alphaMap.needsUpdate = true;


// Function to create a circular alpha map
function createCircularAlphaMap(size) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  context.fillStyle = 'black';
  context.fillRect(0, 0, size, size);
  context.fillStyle = 'white';
  context.beginPath();
  context.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  context.fill();
  return new Texture(canvas);
}

function createUnitCircleGeometry(segments = 64) {
  const pts = [];
  const segs = Math.max(8, Number(segments) || 64);
  for (let i = 0; i <= segs; i += 1) {
    const a = (i / segs) * Math.PI * 2;
    pts.push(new Vector3(Math.cos(a), Math.sin(a), 0));
  }
  const geom = new BufferGeometry();
  geom.setFromPoints(pts);
  return geom;
}

function storyTypeColorHex(storyType) {
  const t = String(storyType || '').trim().toUpperCase();
  if (!t) return '#ffb300';
  // Keep a small stable palette for recognizable overlays.
  if (t === 'FUNNEL') return '#4d79ff';
  if (t === 'CLUSTER') return '#9ef7c5';
  if (t === 'LOOP') return '#ffe55c';
  if (t === 'MIRROR') return '#b45aff';
  if (t === 'STAGING') return '#00ffa0';
  if (t === 'HOTLINKS') return '#ff6b6b';
  if (t === 'WHALE') return '#ffd700';
  if (t === 'SURGE') return '#00c2ff';
  if (t === 'CATEGORY') return '#cfd3ff';
  if (t === 'DISCOVERY') return '#ff8bd4';
  if (t === 'SANDWICH') return '#ffa500';
  if (t === 'THESIS') return '#cfd3ff';
  if (t === 'FLOW') return '#00c2ff';
  // Fallback: stable hash.
  return hashColorHex(t);
}

const STORY_OVERLAY_BLUE = '#4d79ff';

// Simple helper to shorten long addresses for display in labels. Keeps the
// first 6 and last 4 characters, matching the style used in the NodeList so
// graph labels and table rows stay visually consistent.
function formatShortAddress(addr) {
  if (!addr || typeof addr !== 'string') return addr || '';
  const trimmed = addr.trim();
  if (trimmed.length <= 14) return trimmed;
  return `${trimmed.slice(0, 6)}...${trimmed.slice(-4)}`;
}

function hashColorHex(str) {
  const s = String(str || '');
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // Use hue-based hashing to guarantee visually distinct colors.
  const hue = ((h >>> 0) % 360) / 360;
  const c = new Color();
  c.setHSL(hue, 0.75, 0.55);
  return `#${c.getHexString()}`;
}

function createBoundingBoxForce(halfExtent = 2000) {
  let nodes = [];
  let strength = 0.15;
  let damping = 0.85;
  let hardLimit = halfExtent * 2;

  const clamp = (v, lim) => Math.max(-lim, Math.min(lim, v));

  function force() {
    for (let i = 0; i < nodes.length; i += 1) {
      const n = nodes[i];
      if (!n) continue;

      const x = Number(n.x) || 0;
      const y = Number(n.y) || 0;
      const z = Number(n.z) || 0;

      const dx = Math.max(0, Math.abs(x) - halfExtent);
      const dy = Math.max(0, Math.abs(y) - halfExtent);
      const dz = Math.max(0, Math.abs(z) - halfExtent);

      // Only act on outliers beyond the extent so we don't change the dense core.
      if (dx <= 0 && dy <= 0 && dz <= 0) continue;

      if (dx > 0) {
        const s = -Math.sign(x) * dx * strength;
        n.vx = (Number(n.vx) || 0) + s;
      }
      if (dy > 0) {
        const s = -Math.sign(y) * dy * strength;
        n.vy = (Number(n.vy) || 0) + s;
      }
      if (dz > 0) {
        const s = -Math.sign(z) * dz * strength;
        n.vz = (Number(n.vz) || 0) + s;
      }

      // Damping only for outliers to prevent "runaway" nodes.
      if (Number.isFinite(n.vx)) n.vx *= damping;
      if (Number.isFinite(n.vy)) n.vy *= damping;
      if (Number.isFinite(n.vz)) n.vz *= damping;

      // Safety net: prevent extreme numerical blowups.
      if (Number.isFinite(n.x)) n.x = clamp(n.x, hardLimit);
      if (Number.isFinite(n.y)) n.y = clamp(n.y, hardLimit);
      if (Number.isFinite(n.z)) n.z = clamp(n.z, hardLimit);
    }
  }

  force.initialize = (_nodes) => {
    nodes = _nodes || [];
  };

  force.strength = (v) => {
    if (!arguments.length) return strength;
    strength = Number(v) || 0;
    return force;
  };

  force.damping = (v) => {
    if (!arguments.length) return damping;
    damping = Number(v) || 0;
    return force;
  };

  force.hardLimit = (v) => {
    if (!arguments.length) return hardLimit;
    hardLimit = Number(v) || hardLimit;
    return force;
  };

  return force;
}


const normalizeSize = (nodeCount, minNodeCount, maxNodeCount, minSize, maxSize) => {
  return ((maxSize - minSize) * (nodeCount - minNodeCount) / (maxNodeCount - minNodeCount)) + minSize;
};


const isVideo = async (url) => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    const contentType = response.headers.get('Content-Type');
    return contentType && contentType.startsWith('video/');
  } catch (err) {
    //console.error('Failed to check if file is video', err);
    return false;
  }
};

/**
 * Generates properties for the ForceGraph component based on mode and event handlers.
 * @param {boolean} is3DMode - Determines if the graph is in 3D or 2D mode.
 * @param {Object} graphData - The data structure containing nodes and links for the graph.
 * @param {Object} graphRef - Reference to the graph component.
 * @param {Function} handleBackgroundClick - Handler for background click events.
 * @param {Function} handleNodeDragEnd - Handler for node drag end events.
 * @param {Function} handleNodeDrag - Handler for node drag events.
 * @param {Function} handleEngineStop - Handler for engine stop events.
 * @param {Function} handleZoom - Handler for zoom events.
 * @param {Function} handleClick - Handler for click events on nodes or links.
 * @param {Function} handleNodeRightClick - Handler for node right click events.
 * @param {Function} handleNodeDoubleClick - Handler for node double click events.
 * @param {string} dataSource - The data source of the graph.
 * @param {boolean} filterNFT - Whether to filter NFTs from the graph.
 * @param {number} maxValue - The maximum value of the graph.
 * @param {number} imageSize - The size of the image.
 * @param {Function} stopCameraSpin - Function to stop the camera spin.
 * @param {Function} setActiveLinkId - Function to set the active link ID.
 * @param {Function} handleNodeClick - Handler for node click events.
 * @param {boolean} showText - Whether to show token text labels on the graph.
 * @param {boolean} showAddressLabels - Whether to show address labels for non-icon nodes.
 * @param {boolean} showChainLabels - Whether to show chain labels on the graph.
 * @param {string} currentView - Current route pathname (e.g. '/links', '/token').
 * @returns {Object} - Properties to pass to the ForceGraph component.
 */
const getForceGraphProps = (
  isMobile,
  is3DMode,
  graphData,
  hiddenNodeIds,
  graphRef,
  handleBackgroundClick,
  handleNodeDragEnd,
  handleNodeDrag,
  handleEngineStop,
  handleZoom,
  handleClick,
  handleNodeRightClick,
  handleNodeDoubleClick,
  dataSource,
  filterNFT,
  maxValue,
  imageSize,
  stopCameraSpin,
  setActiveLinkId,
  handleNodeClick,
  showText,
  showAddressLabels,
  showNames,
  showChainLabels,
  currentView,
  pcapGroupEnabled,
  pcapLinkStyle,
  pcapShowLabels,
  pcapLabelSize,
  labelSize,
  pcapSelectedLinkKeys,
  storyNodeMap,
  onStoryNodeClick,
  watchedKeys,
  storyOverlayEnabled,
  storyOverlayMap,
  imageCacheRef,
  failedImageUrlsRef,
) => {
  const safeCurrentView = (v) => {
    try {
      if (typeof v === 'string') return v;
      if (v && typeof v === 'object') {
        const p = v.pathname;
        if (typeof p === 'string') return p;

        const loc = v.location;
        if (loc && typeof loc === 'object' && typeof loc.pathname === 'string') {
          return loc.pathname;
        }
      }
      if (typeof window !== 'undefined' && window.location && typeof window.location.pathname === 'string') {
        return window.location.pathname;
      }
      return '';
    } catch (e) {
      try {
        if (typeof window !== 'undefined' && window.location && typeof window.location.pathname === 'string') {
          return window.location.pathname;
        }
      } catch (e2) {
      }
      return '';
    }
  };

  const currentViewStr = safeCurrentView(currentView);

  const storyOverlayLookup = (() => {
    if (!storyOverlayEnabled) return null;
    if (!storyOverlayMap || typeof storyOverlayMap.get !== 'function') return null;
    return storyOverlayMap;
  })();

  const getOverlayStory = (node) => {
    if (!storyOverlayLookup || !node) return null;
    const id = node.id != null ? String(node.id).toLowerCase() : '';
    if (!id) return null;
    const arr = storyOverlayLookup.get(id);
    if (!arr || !arr.length) return null;
    return arr[0];
  };

  const normalizeEndpointId = (v) => {
    try {
      if (v == null) return '';
      if (typeof v === 'object') {
        const raw = v.id ?? v._id ?? v.address;
        return raw != null ? String(raw) : '';
      }
      return String(v);
    } catch (e) {
      return '';
    }
  };

  const isSelectedPcapLink = (link) => {
    try {
      if (!pcapSelectedLinkKeys || typeof pcapSelectedLinkKeys.has !== 'function') return false;
      if (!link || link.metaType !== 'pcap') return false;
      const s = normalizeEndpointId(link.source);
      const t = normalizeEndpointId(link.target);
      if (!s || !t) return false;
      const ordered = [String(s), String(t)].sort();
      const key = `${ordered[0]}↔${ordered[1]}`;
      return pcapSelectedLinkKeys.has(key);
    } catch (e) {
      return false;
    }
  };
  const allNamesIdentical = (() => {
    if (!graphData || !Array.isArray(graphData.nodes) || !graphData.nodes.length) {
      return false;
    }

    const names = graphData.nodes
      .map((n) => (n && typeof n.label === 'string' ? n.label.trim() : ''))
      .filter((l) => l && !l.startsWith('0x'));

    if (names.length < 2) return false;
    const first = names[0];
    return names.every((l) => l === first);
  })();

  const pcapSizeStats = (() => {
    if (dataSource !== PCAP) return null;
    const arr = graphData && Array.isArray(graphData.nodes) ? graphData.nodes : [];
    if (!arr.length) return { min: 1, max: 1 };
    let min = Infinity;
    let max = 0;
    arr.forEach((n) => {
      // In group mode, size by aggregated traffic (totalBytes) so node sizes
      // remain meaningful but bounded.
      const metric = pcapGroupEnabled
        ? (Number(n && (n.totalBytes ?? n.total_bytes ?? n.bytes ?? 0)) || 0)
        : (Number(n && (n.linkCount ?? n.degree ?? n.count ?? 0)) || 0);
      min = Math.min(min, metric);
      max = Math.max(max, metric);
    });
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= 0) return { min: 1, max: 1 };
    if (min === Infinity) min = 1;
    if (max < min) max = min;
    return { min, max };
  })();

  const formatBytes = (bytes) => {
    const b = Number(bytes) || 0;
    const abs = Math.abs(b);
    if (abs >= 1024 * 1024 * 1024) return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    if (abs >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(2)} MB`;
    if (abs >= 1024) return `${(b / 1024).toFixed(2)} KB`;
    return `${Math.round(b)} B`;
  };

  const getPcapPairTotals = (link) => {
    const s = typeof link.source === 'object' ? link.source.id : link.source;
    const t = typeof link.target === 'object' ? link.target.id : link.target;
    const a = String(s || '');
    const b = String(t || '');
    const ordered = [a, b].sort();
    const key = `${ordered[0]}↔${ordered[1]}`;
    let tx = 0;
    let rx = 0;

    const links = (graphData && Array.isArray(graphData.links)) ? graphData.links : [];
    for (let i = 0; i < links.length; i += 1) {
      const l = links[i];
      if (!l || l.metaType !== 'pcap') continue;
      const ss = typeof l.source === 'object' ? l.source.id : l.source;
      const tt = typeof l.target === 'object' ? l.target.id : l.target;
      const oo = [String(ss || ''), String(tt || '')].sort();
      const kk = `${oo[0]}↔${oo[1]}`;
      if (kk !== key) continue;

      const dir = (l.pcapDir || '').toString().toLowerCase();
      const v = Number(l.value) || 0;
      if (dir === 'tx') tx += v;
      else if (dir === 'rx') rx += v;
      else {
        tx += Number(l.txBytes) || 0;
        rx += Number(l.rxBytes) || 0;
      }
    }

    return { tx, rx };
  };

  const commonProps = {
    ref: graphRef,
    graphData: graphData,
    nodeId: 'id',
    linkSource: 'source',
    linkTarget: 'target',
    nodeVisibility: (node) => {
      try {
        if (!hiddenNodeIds || typeof hiddenNodeIds.has !== 'function') return true;
        const id = node && node.id != null ? String(node.id) : '';
        if (!id) return true;
        return !hiddenNodeIds.has(id);
      } catch (e) {
        return true;
      }
    },
    linkVisibility: (link) => {
      try {
        if (!hiddenNodeIds || typeof hiddenNodeIds.has !== 'function') return true;
        const s = link && (typeof link.source === 'object' ? link.source.id : link.source);
        const t = link && (typeof link.target === 'object' ? link.target.id : link.target);
        const sid = s != null ? String(s) : '';
        const tid = t != null ? String(t) : '';
        if (sid && hiddenNodeIds.has(sid)) return false;
        if (tid && hiddenNodeIds.has(tid)) return false;
        return true;
      } catch (e) {
        return true;
      }
    },
    nodeLabel: (dataSource === PCAP)
      ? ((node) => {
        try {
          if (!node) return '';
          const disp = node.displayId != null ? String(node.displayId).trim() : '';
          if (disp) return disp;
          if (!showNames || allNamesIdentical) return String(node.id || '');
          return String((node.label != null && String(node.label).trim()) ? node.label : (node.id || ''));
        } catch (e) {
          return String(node && node.id ? node.id : '');
        }
      })
      : ((node) => {
        try {
          if (!node) return '';
          // In Stories mode, show story context as tooltip for story-address nodes.
          if (storyNodeMap && storyNodeMap.size > 0) {
            const key = String(node.id || '').toLowerCase();
            const stories = storyNodeMap.get(key);
            if (stories && stories.length > 0) {
              const lines = stories.slice(0, 3).map(s => {
                const type = s.story_type ? `[${String(s.story_type).toUpperCase()}]` : '';
                const title = s.title ? String(s.title) : '';
                const score = s.score != null ? ` · Score ${s.score}` : '';
                return `${type} ${title}${score}`.trim();
              });
              return `<div style="text-align:left">${lines.join('<br/>')}</div>`;
            }
          }
          const base = (!showNames || allNamesIdentical) ? (node.id || '') : ((node.label != null && String(node.label).trim()) ? node.label : (node.id || ''));
          return String(base);
        } catch (e) {
          return String(node && node.id ? node.id : '');
        }
      }),
    nodeAutoColorBy: (dataSource === PCAP) ? undefined : "group",
    nodeColor: (node) => {
      if (dataSource === PCAP) {
        if (node && typeof node.color === 'string' && String(node.color).trim()) {
          return String(node.color).trim();
        }
        if (node && typeof node.color === 'number' && Number.isFinite(node.color)) {
          return '#' + (node.color >>> 0).toString(16).padStart(6, '0');
        }
        return hashColorHex(node && node.id);
      }
      return node && node.color ? node.color : undefined;
    },
    linkLabel: (link) => {
      if (!(dataSource === PCAP && link && link.metaType === 'pcap')) return '';
      const nodeDisplayById = (() => {
        const map = new Map();
        const nodes = graphData && Array.isArray(graphData.nodes) ? graphData.nodes : [];
        for (let i = 0; i < nodes.length; i += 1) {
          const n = nodes[i];
          if (!n || n.id == null) continue;
          const id = String(n.id);
          const display = (n.displayId != null && String(n.displayId).trim())
            ? String(n.displayId)
            : ((n.label != null && String(n.label).trim()) ? String(n.label) : id);
          map.set(id, display);
        }
        return map;
      })();
      const { tx, rx } = getPcapPairTotals(link);
      const s = typeof link.source === 'object' ? link.source.id : link.source;
      const t = typeof link.target === 'object' ? link.target.id : link.target;
      const sId = s != null ? String(s) : '';
      const tId = t != null ? String(t) : '';
      const sDisp = nodeDisplayById.get(sId) || sId;
      const tDisp = nodeDisplayById.get(tId) || tId;
      return `${sDisp} ↔ ${tDisp}\nTX: ${formatBytes(tx)}\nRX: ${formatBytes(rx)}`;
    },
  };
  
  if (is3DMode) {
    return {
      ...commonProps,
      // PCAP: directional particles tend to create a bright "hairball" in
      // dense hubs and can be mistaken for mis-connected links.
      linkDirectionalParticles: (dataSource === PCAP) ? 0 : 2,
      linkDirectionalParticleSpeed: 0.008,
      linkCurvature: (link) => {
        if (link && link.metaType === 'deep_dive') return 0.28;
        if (dataSource === PCAP && link && typeof link.curvature === 'number') {
          // Default PCAP rendering should use straight links. Only curve links
          // in split mode (tx/rx) where we intentionally draw parallel arcs.
          if (pcapLinkStyle !== 'split_curved') {
            return 0;
          }
          const dir = (link.pcapDir || '').toString().toLowerCase();
          const s = typeof link.source === 'object' ? link.source.id : link.source;
          const t = typeof link.target === 'object' ? link.target.id : link.target;

          const base = Math.abs(link.curvature);
          const key = `${s || ''}|${t || ''}|${dir}`;
          let h = 2166136261;
          for (let i = 0; i < key.length; i += 1) {
            h ^= key.charCodeAt(i);
            h = Math.imul(h, 16777619);
          }
          // Add slight, stable variation so dense hub bundles don't stack.
          const jitter = ((h >>> 0) % 100) / 100; // 0..0.99
          const scale = 0.85 + jitter * 0.35; // 0.85..1.20
          return base * scale;
        }
        return 0;
      },
      linkCurveRotation: (link) => {
        if (dataSource === PCAP && link && link.metaType === 'pcap') {
          if (pcapLinkStyle !== 'split_curved') {
            return 0;
          }

          const dir = (link.pcapDir || '').toString().toLowerCase();
          const s = typeof link.source === 'object' ? link.source.id : link.source;
          const t = typeof link.target === 'object' ? link.target.id : link.target;
          const key = `${s || ''}|${t || ''}|${dir}`;
          let h = 2166136261;
          for (let i = 0; i < key.length; i += 1) {
            h ^= key.charCodeAt(i);
            h = Math.imul(h, 16777619);
          }
          const bucket = (h >>> 0) % (pcapGroupEnabled ? 16 : 8);
          const baseRot = bucket * (Math.PI / (pcapGroupEnabled ? 8 : 4));

          // Also flip opposing direction so A->B and B->A are on different planes.
          const dirRot = (dir === 'rx') ? Math.PI : 0;
          return baseRot + dirRot;
        }

        if (!(link && link.metaType === 'deep_dive')) return 0;
        const dd = (link.ddType || link.dd_type || link.type || '').toString().toUpperCase();
        if (dd === 'COUNTERPARTY_WALK') return Math.PI;
        return 0;
      },
      linkOpacity: 1,
      backgroundColor: "#000000",
      onBackgroundClick: (...args) => {
        if (stopCameraSpin) {
          stopCameraSpin();
        }
        if (handleBackgroundClick) {
          handleBackgroundClick(...args);
        }
      },
      onNodeDragEnd: (...args) => {
        // Resume simulation after drag so layout can continue evolving.
        if (graphRef?.current && graphRef.current.resumeAnimation) {
          try {
            graphRef.current.resumeAnimation();
          } catch (e) {
            console.warn('resumeAnimation error on drag end:', e);
          }
        }

        if (stopCameraSpin) {
          stopCameraSpin();
        }
        if (handleNodeDragEnd) {
          handleNodeDragEnd(...args);
        }
      },
      onNodeDrag: (...args) => {
        // Pause simulation while dragging so only the dragged node moves
        // and connected nodes do not follow.
        if (graphRef?.current && graphRef.current.pauseAnimation) {
          try {
            graphRef.current.pauseAnimation();
          } catch (e) {
            console.warn('pauseAnimation error during drag:', e);
          }
        }

        if (stopCameraSpin) {
          stopCameraSpin();
        }
        if (handleNodeDrag) {
          handleNodeDrag(...args);
        }
      },
      onEngineStop: (...args) => {
        if (handleEngineStop) handleEngineStop(...args);
      },
      onZoom: (...args) => {
        if (stopCameraSpin) {
          stopCameraSpin();
        }
        if (handleZoom) {
          handleZoom(...args);
        }
      },
      onClick: (...args) => {
        if (stopCameraSpin) {
          stopCameraSpin();
        }
        if (handleClick) {
          handleClick(...args);
        }
      },
      onNodeRightClick: (node, event) => {
        if (handleNodeRightClick) {
          handleNodeRightClick(node, event);
        }
      },

      d3Force: (forceGraphInstance) => {
        const segs = currentViewStr.split('/').filter(Boolean);
        const isStoriesInline = segs[0] === 'stories';
        const isLinksView = segs[0] === 'links' || segs[0] === 'graphs';

        // Build a simple degree map from current links so both layouts can
        // use degree-aware distances.
        const degreeMap = new Map();
        if (graphData && Array.isArray(graphData.links)) {
          graphData.links.forEach((l) => {
            const s = typeof l.source === 'object' ? l.source.id : l.source;
            const t = typeof l.target === 'object' ? l.target.id : l.target;
            if (!degreeMap.has(s)) degreeMap.set(s, 0);
            if (!degreeMap.has(t)) degreeMap.set(t, 0);
            degreeMap.set(s, degreeMap.get(s) + 1);
            degreeMap.set(t, degreeMap.get(t) + 1);
          });
        }

        if (dataSource === PCAP) {
          // PCAP: keep graphs compact and prevent low-degree stragglers from
          // drifting far away.
          try {
            const groupOn = !!pcapGroupEnabled;
            forceGraphInstance
              .d3Force('link')
              .distance((link) => {
                const rawS = typeof link.source === 'object' ? link.source.id : link.source;
                const rawT = typeof link.target === 'object' ? link.target.id : link.target;
                const sId = rawS ? String(rawS) : '';
                const tId = rawT ? String(rawT) : '';
                const sDeg = degreeMap.get(sId) || 0;
                const tDeg = degreeMap.get(tId) || 0;
                const deg = Math.max(sDeg, tDeg);
                const base = groupOn ? 260 : 55;
                const perDeg = groupOn ? 14 : 6;
                const cap = groupOn ? 520 : 140;
                return base + Math.min(cap, deg * perDeg);
              });

            const charge = forceGraphInstance.d3Force('charge');
            if (charge && typeof charge.strength === 'function') {
              charge.strength(groupOn ? -9000 : -900);
            }
            if (charge && typeof charge.distanceMax === 'function') {
              charge.distanceMax(groupOn ? 2400 : 420);
            }

            if (typeof forceGraphInstance.d3Force === 'function') {
              // Collision needs to scale with node size; grouped nodes can be
              // significantly larger due to higher effective degree.
              forceGraphInstance.d3Force(
                'collision',
                d3.forceCollide((n) => {
                  if (!groupOn) return 10;
                  const stats = pcapSizeStats || { min: 1, max: 1 };
                  const bytes = Number(n && (n.totalBytes ?? n.total_bytes ?? n.bytes ?? 0)) || 0;
                  // Match the group-mode node radius range (20..100) and add padding.
                  const r = normalizeSize(bytes, stats.min, stats.max, 20, 100);
                  return r + 10;
                })
              );
            }
          } catch (e) {
          }
          return;
        }

        if (isStoriesInline) {
          const baseDistance = 620;
          const extraPerDegree = 22;
          const maxExtra = 520;

          forceGraphInstance
            .d3Force('link')
            .distance((link) => {
              const sId = typeof link.source === 'object' ? link.source.id : link.source;
              const tId = typeof link.target === 'object' ? link.target.id : link.target;
              const sDeg = degreeMap.get(sId) || 0;
              const tDeg = degreeMap.get(tId) || 0;
              const deg = Math.max(sDeg, tDeg);
              const extra = Math.min(deg * extraPerDegree, maxExtra);
              return baseDistance + extra;
            });

          forceGraphInstance
            .d3Force('charge')
            .strength(-1400);

          forceGraphInstance.d3Force('collision', d3.forceCollide(18));
        } else if (isLinksView) {
          const baseDistance = 260;
          const extraPerDegree = 14;
          const maxExtra = 260;

          forceGraphInstance
            .d3Force('link')
            .distance((link) => {
              const sId = typeof link.source === 'object' ? link.source.id : link.source;
              const tId = typeof link.target === 'object' ? link.target.id : link.target;
              const sDeg = degreeMap.get(sId) || 0;
              const tDeg = degreeMap.get(tId) || 0;
              const deg = Math.max(sDeg, tDeg);
              const extra = Math.min(deg * extraPerDegree, maxExtra);
              return baseDistance + extra;
            });

          forceGraphInstance
            .d3Force('charge')
            .strength(-5000);

          try {
            const charge = forceGraphInstance.d3Force('charge');
            if (charge && typeof charge.distanceMax === 'function') {
              charge.distanceMax(1800);
            }
          } catch (e) {
          }

          forceGraphInstance.d3Force('collision', d3.forceCollide(4));

          try {
            const nodeCount = (graphData && Array.isArray(graphData.nodes)) ? graphData.nodes.length : 0;
            const baseExtent = Math.max(1400, Math.min(6000, Math.sqrt(Math.max(1, nodeCount)) * 120));
            const extent = nodeCount > 6000 ? Math.min(baseExtent, 2600) : baseExtent;
            const bounds = createBoundingBoxForce(extent);
            // Tuning: keep the core intact; just rein in stragglers.
            bounds.strength(nodeCount > 6000 ? 0.22 : 0.15);
            bounds.damping(0.82);
            bounds.hardLimit(extent * 3);
            forceGraphInstance.d3Force('bounds', bounds);
          } catch (e) {
          }
        } else {
          // Organic layouts for non-stories views.
          const baseDistance = 1600;
          const extraPerDegree = 40;
          const maxExtra = 800;

          forceGraphInstance
            .d3Force('link')
            .distance((link) => {
              const sId = typeof link.source === 'object' ? link.source.id : link.source;
              const tId = typeof link.target === 'object' ? link.target.id : link.target;
              const sDeg = degreeMap.get(sId) || 0;
              const tDeg = degreeMap.get(tId) || 0;
              const deg = Math.max(sDeg, tDeg);
              const extra = Math.min(deg * extraPerDegree, maxExtra);
              return baseDistance + extra;
            });

          forceGraphInstance
            .d3Force('charge')
            .strength(-500);

          // Collision force to keep nodes from overlapping in the dense core.
          // Allow more interpenetration in the galaxy view so clusters
          // can spread without forming one solid block.
          forceGraphInstance.d3Force('collision', d3.forceCollide(60));
        }
      },

      // Let the simulation refine the layout without running excessively long.
      // PCAP graphs can be extremely dense; add extra damping so nodes settle
      // and don't "vibrate" for long periods.
      d3AlphaDecay: (dataSource === PCAP) ? (!!pcapGroupEnabled ? 0.02 : 0.06) : 0.02,
      d3VelocityDecay: (dataSource === PCAP) ? (!!pcapGroupEnabled ? 0.25 : 0.55) : 0.3,
      d3AlphaMin: (dataSource === PCAP) ? (!!pcapGroupEnabled ? 0.008 : 0.03) : 0.01,

      linkColor: (link) => {
        if (dataSource === PCAP && isSelectedPcapLink(link)) {
          return 'rgba(33, 150, 243, 0.95)';
        }

        // Highlight freshly added links for a brief period using appearedAt,
        // falling back to the standard 3D link color otherwise.
        const nowSec = Date.now() / 1000;
        const highlightWindow = 1.0; // seconds
        if (link.appearedAt && (nowSec - link.appearedAt) < highlightWindow) {
          return 'rgba(255, 255, 255, 1)';
        }

        if (dataSource === PCAP && link && link.metaType === 'pcap') {
          if (pcapLinkStyle === 'collapsed_straight') {
            return pcapGroupEnabled ? 'rgba(255, 255, 0, 0.07)' : 'rgba(255, 255, 0, 0.22)';
          }
          const dir = (link.pcapDir || '').toString().toLowerCase();
          const a = pcapGroupEnabled ? 0.10 : 0.35;
          if (dir === 'rx') return `rgba(0, 229, 255, ${a})`;
          if (dir === 'tx') return `rgba(255, 109, 0, ${a})`;
          return pcapGroupEnabled ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.22)';
        }

        // Prefer per-link color when provided (e.g. deep-dive trails).
        if (link && link.color) {
          return link.color;
        }

        return 'rgba(255, 255, 0, 0.15)'; // base color (opacity handled by linkMaterial)
      },

      // Force Three.js to actually respect translucency for base links.
      // (Some force-graph builds ignore alpha in linkColor unless material is transparent.)
      linkMaterial: (link) => {
        if (link && link.metaType === 'deep_dive') {
          const c = (link && link.color) ? link.color : '#ffffff';
          return new LineBasicMaterial({ color: c, transparent: true, opacity: 0.95 });
        }

        // For non-PCAP graphs, we tag neighbor-selected links with __neighborSelected.
        // Render them with a more visible material than the default base link opacity.
        if (dataSource !== PCAP && link && link.__neighborSelected) {
          const c = (link && link.color) ? link.color : '#2196f3';
          return new LineBasicMaterial({ color: c, transparent: true, opacity: 0.85 });
        }

        if (dataSource === PCAP && isSelectedPcapLink(link)) {
          return new LineBasicMaterial({ color: '#2196f3', transparent: true, opacity: 0.95 });
        }

        if (dataSource === PCAP && link && link.metaType === 'pcap') {
          const computePcapOpacity = () => {
            // In grouped mode, many links overlap near hubs. If opacity is too low,
            // you only see a small bright cluster near the hub and not the long
            // connections reaching other nodes. Scale opacity by link value so
            // stronger links are visible without saturating the core.
            const v = Number(link && link.value) || 0;
            const mv = Number(maxValue) || 0;
            const denom = Math.log((mv > 0 ? mv : 1) + 1);
            const t = denom > 0 ? (Math.log(v + 1) / denom) : 0;
            const clamped = Math.max(0, Math.min(1, t));
            // Diagnostic: make grouped links visibly reach their targets.
            // If links still look disconnected at these opacities, geometry/endpoints are wrong.
            return 0.35 + clamped * 0.55;
          };

          if (pcapLinkStyle === 'collapsed_straight') {
            const op = pcapGroupEnabled ? (computePcapOpacity() * 0.9) : 0.22;
            const m = new LineBasicMaterial({ color: '#ffff00', transparent: true, opacity: op });
            if (pcapGroupEnabled) m.depthWrite = false;
            return m;
          }
          const dir = (link.pcapDir || '').toString().toLowerCase();
          const c = (dir === 'rx') ? '#00e5ff' : (dir === 'tx') ? '#ff6d00' : '#ffffff';
          const op = pcapGroupEnabled ? computePcapOpacity() : 0.35;
          const m = new LineBasicMaterial({ color: c, transparent: true, opacity: op });
          if (pcapGroupEnabled) m.depthWrite = false;
          return m;
        }

        // Base links: keep translucent.
        const c = (link && link.color) ? link.color : '#ffff00';
        return new LineBasicMaterial({ color: c, transparent: true, opacity: 0.15 });
      },

      onNodeClick: (node, e) => {
        stopCameraSpin();

        const hasEvent = !!e;
        const shiftKey = hasEvent && !!e.shiftKey;
        const ctrlKey = hasEvent && !!e.ctrlKey;

        // Treat a double-click as a separate gesture so the app can
        // follow transactions without interfering with single-click
        // behavior. ForceGraph's event detail may not carry a reliable
        // click count, so approximate using time between clicks on the
        // same node.
        const now = Date.now();
        const DOUBLE_CLICK_MS = 350;
        let isDoubleClick = false;

        if (lastClickNodeId === node.id &&
            lastClickTime &&
            (now - lastClickTime) <= DOUBLE_CLICK_MS) {
          isDoubleClick = true;
        }

        lastClickNodeId = node.id;
        lastClickTime = now;

        if (handleNodeDoubleClick && isDoubleClick) {
          handleNodeDoubleClick(node, e);
          return;
        }

        // In Stories mode, intercept clicks on story-address nodes to show
        // the story popover. Use the mouse event coordinates for positioning.
        if (onStoryNodeClick && !shiftKey && !ctrlKey) {
          const sx = hasEvent && e.clientX != null ? e.clientX : window.innerWidth / 2;
          const sy = hasEvent && e.clientY != null ? e.clientY : window.innerHeight / 2;
          const consumed = onStoryNodeClick(node.id, sx, sy);
          if (consumed) return;
        }

        // Best-effort clipboard copy; skip if document not focused or
        // Clipboard API is unavailable / denied.
        try {
          if (typeof document !== 'undefined' && document.hasFocus && document.hasFocus() &&
              navigator && navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(node.id).catch(err => {
              console.warn('Clipboard write failed:', err);
            });
          }
        } catch (err) {
          console.warn('Clipboard write threw:', err);
        }
        if (dataSource !== PCAP) {
          // Determine explorer base URL from node.chain when available.
          const chain = (node.chain || '').toUpperCase();
          let explorerBaseUrl = 'https://etherscan.io';
          if (chain === 'SOL') explorerBaseUrl = 'https://solscan.io';
          else if (chain === 'BSC') explorerBaseUrl = 'https://bscscan.com';
          else if (chain === 'ARB') explorerBaseUrl = 'https://arbiscan.io';
          else if (chain === 'OPT') explorerBaseUrl = 'https://optimistic.etherscan.io';
          else if (chain === 'POL' || chain === 'MATIC') explorerBaseUrl = 'https://polygonscan.com';
          else if (chain === 'AVAX') explorerBaseUrl = 'https://snowtrace.io';

          if (shiftKey && (dataSource === ETHERSCAN || chain === 'ETH' || !chain)) {
            // Preserve Uniswap shortcut for ETH mainnet tokens.
            let newId = node.id;
            if (newId.includes('.')) {
              newId = newId.split('.')[0];
            }
            const url = `https://etherscan.io/dex/uniswapv2/${newId}`;
            window.open(url, '_blank');
          }
          else if (ctrlKey) {
            let middle = '/address/';
            let newId = node.id;

            // If it is an NFT we added a period and the token id onto the address
            if (newId.includes('.')) {
              middle = '/nft/';
              newId = newId.replace('.', '/');
            }

            const url = explorerBaseUrl + middle + newId;
            window.open(url, '_blank');
          }
          else {
            handleNodeClick(dataSource, graphData, graphRef, node.id, false);
          }

        } else {
          handleNodeClick(dataSource, graphData, graphRef, node.id, false);
        }
      },

      onLinkClick: (link) => {
        if (dataSource !== PCAP) {
          const explorerBaseUrl = (dataSource === ETHERSCAN) ? 'https://etherscan.io' : 'https://bscscan.com';
          const url = explorerBaseUrl + '/tx/' + link.tx;
          window.open(url, '_blank');
        } else {
          handleNodeClick(dataSource, graphData, graphRef, link.target.id, false);
          setActiveLinkId(link.target.id);
        }
      },

      linkWidth: (link) => {
        const segs = currentViewStr.split('/').filter(Boolean);
        const isStoriesInline = segs[0] === 'stories';
        const isLinksView = segs[0] === 'links' || segs[0] === 'graphs';

        if (dataSource === PCAP && link && link.metaType === 'pcap') {
          return pcapGroupEnabled ? 0.55 : 1.15;
        }

        // For the compact /stories view, use a uniform thin link so the
        // dense sphere remains readable and nodes/icons are visually
        // prioritized over edges.
        if (isStoriesInline) {
          return 1;
        }

        // Original width behavior for /links and other main views.
        // Keep /links thinner so dense graphs remain readable.
        const minWidth = isLinksView ? 0.5 : 1;
        const maxWidth = isLinksView ? 2.0 : 5;
        const size = link.value ? Math.log(link.value + 1) : 1; // +1 to avoid log(0), ensures all values are greater than 0
        const logMaxValue = Math.log(maxValue + 1); // Apply the same transformation to maxValue
        let width = Math.min(Math.max((size / logMaxValue) * (maxWidth - minWidth) + minWidth, minWidth), maxWidth);

        const nowSec = Date.now() / 1000;
        const highlightWindow = 1.0;
        if (link.appearedAt && (nowSec - link.appearedAt) < highlightWindow) {
          width *= isLinksView ? 1.25 : 1.8; // make fresh links noticeably thicker
        }

        return width;
    },


    nodeThreeObject: (node) => {
      // Synthetic chain label nodes: render a large semi-transparent sphere
      // with a big chain name label so each chain sphere is clearly
      // identified in 3D.
      if (showChainLabels && node.nodeType === 'chain_label') {
        const base = node.labelRadius || 120;
        const radius = base * 0.9;
        const colorHex = '#' + node.color.toString(16).padStart(6, '0');

        const geom = new SphereGeometry(radius, 32, 32);
        const mat = new MeshPhongMaterial({
          color: parseInt(colorHex.slice(1), 16),
          transparent: true,
          opacity: 0.25,
        });
        const mesh = new Mesh(geom, mat);

        const text = new SpriteText(node.label || node.chain || '');
        // Use the same chain color for the text so the label matches the
        // sphere. Make it slightly smaller so it does not overpower the
        // tokens while remaining easy to read.
        text.color = colorHex;
        text.textHeight = radius * 0.5;
        text.material.transparent = false;
        text.material.opacity = 1.0;
        mesh.add(text);

        return mesh;
      }

      // Base node size, then increase by link count (degree), capped so that
      // very high-degree nodes don't explode in size. We already store
      // node.linkCount in App.js when building the graph.
      const nodeCount = graphData.nodes.length;
      const degree = node.linkCount || 0;


      let baseSize = 30;

      const segs = currentViewStr.split('/').filter(Boolean); // ['links','0x...'] or ['token','0x...'] or ['stories']
      const isStoriesInline = segs[0] === 'stories';
      const isLinksView = segs[0] === 'links' || segs[0] === 'graphs';
      const isLinksDetail = (segs[0] === 'links' || segs[0] === 'graphs') && segs.length >= 2;
      const isTokenDetail = segs[0] === 'token' && segs.length >= 2;
      const isTokenRoot = segs[0] === 'token' && segs.length === 1;
      const isLinksRoot = (segs[0] === 'links' || segs[0] === 'graphs') && segs.length === 1;


      if (node.icon) {
        if (dataSource === PCAP) {
          // PCAP graphs can have very large ranges; normalize to a sane radius.
          const stats = pcapSizeStats || { min: 1, max: 1 };
          const d = Number(degree || 0);
          baseSize = normalizeSize(d, stats.min, stats.max, 7, 56);
        } else if (isTokenDetail || isLinksDetail) {
          // For /token/<id> and /links address/name deep dives, keep token
          // icons reasonably sized so labels remain readable even when many
          // nodes are present.
          baseSize = 40;
        } else if (isTokenRoot) {
          // Restore a more prominent size for the /token overview so token
          // icons don't feel tiny relative to their labels.
          baseSize = 70;
        }
      }


      // Increase size by the number of links for this node, capped at +20
      let extraSize = Math.min(degree, 100);

      if (dataSource === PCAP) {
        const stats = pcapSizeStats || { min: 1, max: 1 };
        const metric = pcapGroupEnabled
          ? (Number(node && (node.totalBytes ?? node.total_bytes ?? node.bytes ?? 0)) || 0)
          : Number(degree || 0);

        // Group mode: normalize aggregated traffic into a bounded, readable range.
        // Non-group: keep the existing degree-based sizing.
        baseSize = normalizeSize(metric, stats.min, stats.max, 5, 40);
        extraSize = 0;
      }

      // For the inline stories forensics view, keep nodes very small so the
      // sphere doesn't become a solid ball. Give a bit more prominence to
      // nodes with icons and shrink address-only nodes.
      if (isStoriesInline) {
        if (node.icon) {
          baseSize = 7;                  // slightly smaller for tokens with icons
          extraSize = Math.min(degree, 10);
        } else {
          baseSize = 3;                  // plain addresses stay very small
          extraSize = Math.min(degree, 6);
        }
      }

      // For /links, match the compact forensics sizing so the graph can
      // expand naturally without turning into overlapping giant spheres.
      // (The old /links sizing made moderate-degree nodes huge.)
      if (isLinksView && !isStoriesInline) {
        if (node.icon) {
          baseSize = 9;
          extraSize = Math.min(degree, 10);
        } else {
          baseSize = 4;
          extraSize = Math.min(degree, 6);
        }
      }

      // In the root /links galaxy view, de-emphasize anonymous address
      // nodes (no icon and label that looks like a raw 0x address) so the
      // named/icon tokens stand out visually.
      if (isLinksRoot && !isStoriesInline && !node.icon) {
        const labelStr = (node.label && typeof node.label === 'string') ? node.label.trim() : '';
        const looksLikeAddress = labelStr.startsWith('0x') && labelStr.length > 10;
        if (looksLikeAddress) {
          baseSize *= 0.35;
          extraSize = Math.min(degree, 40) * 0.35;
        }
      }

      let size = baseSize + extraSize;


      // In deep-dive detail views with many nodes (address/name searches),
      // shrink overall node size as nodeCount grows so the scene does not
      // become a wall of icons and labels stay legible.
      const isDetailView = (isLinksDetail || isTokenDetail) && !isStoriesInline;
      if (isDetailView) {
        if (nodeCount > 800) {
          size *= 0.25;
        } else if (nodeCount > 400) {
          size *= 0.4;
        } else if (nodeCount > 200) {
          size *= 0.6;
        }
      }

      // In single-token /links searches (allNamesIdentical is true), plain
      // address nodes can be extremely numerous. Give them a small boost so
      // they are not tiny compared to the central icon while still keeping
      // the overall sphere readable.
      if (!node.icon && allNamesIdentical && (segs[0] === 'links' || segs[0] === 'graphs') && !isStoriesInline) {
        size *= 1.5;
      }


      // Scale size based on how aggressively the layout was expanded.
      const expandScale = node.expandScale || 1;
      // Avoid applying expandScale in /links; it causes nodes to balloon and
      // overlap, and we want /links to behave like the forensics summary.
      if (!isLinksView && !(dataSource === PCAP && pcapGroupEnabled)) {
        size *= expandScale;
      }


      // Clamp to a safe finite range so SphereGeometry doesn't attempt to
      // allocate absurdly large buffers if something upstream goes wrong.
      if (!Number.isFinite(size) || size <= 0) {
        size = 20;
      } else {
        size = Math.min(size, 2000);
      }
    
      const segments = isMobile ? 16 : 32;
      const sphereGeometry = new SphereGeometry(size, segments, segments);
      const pcapColor = (dataSource === PCAP)
        ? (() => {
          // PCAP nodes often come in with a uniform color; prefer stable per-id colors.
          // Only honor explicit hex strings if provided.
          if (typeof node.color === 'string' && node.color && String(node.color).trim().startsWith('#')) return String(node.color).trim();
          return hashColorHex(node.id);
        })()
        : node.color;

      const sphereMaterial = new MeshPhongMaterial({
        color: new Color(pcapColor),
        transparent: true,
        opacity: 0.7,
        shininess: 60,
        specular: new Color(0x111111)
      });
    
      const sphere = new Mesh(sphereGeometry, sphereMaterial);
      // Store node size so the story-overlay effect can size rings correctly
      // without needing to re-run the full nodeThreeObject logic.
      sphere.userData.nodeSize = size;

      const rawIcon = node && node.icon != null ? String(node.icon).trim() : '';
      const isPcapIcon =
        (dataSource === PCAP) &&
        !!rawIcon &&
        (
          rawIcon.includes('/api/pcapIcon') ||
          rawIcon.includes('/pcapIcon') ||
          rawIcon.includes('icons.duckduckgo.com')
        );

      const loadAsImage = () => {
        const loader = new TextureLoader();
        // Do NOT set crossOrigin — WebGL texture rendering does not require CORS
        // headers, and setting crossOrigin='anonymous' causes browsers to block
        // images from CDNs like assets.coingecko.com that omit CORS headers.
        const spriteMap = loader.load(
          node.icon,
          () => {
            try {
              if (typeof window !== 'undefined' && window.__DEBUG_PCAP_ICONS__) {
                console.log('[PCAP][icon][graph] image loaded', {
                  id: node && node.id,
                  label: node && node.label,
                  icon: node && node.icon,
                });
              }
            } catch (e) {
            }
          },
          undefined,
          (err) => {
            try {
              if (typeof window !== 'undefined' && window.__DEBUG_PCAP_ICONS__) {
                console.log('[PCAP][icon][graph] image failed', {
                  id: node && node.id,
                  label: node && node.label,
                  icon: node && node.icon,
                  err,
                });
              }
            } catch (e) {
            }
          }
        );
        const spriteMaterial = new SpriteMaterial({
          map: spriteMap,
          alphaMap,
          transparent: true,
          depthTest: false,
          depthWrite: false
        });
        const sprite = new Sprite(spriteMaterial);
        const spriteScale = size * 2;
        sprite.scale.set(spriteScale, spriteScale, 1);
        const zPosition = (dataSource === PCAP)
          ? (isPcapIcon ? 0 : (size + 0.1))
          : (showText ? 0 : size + 0.1);
        sprite.position.set(0, 0, zPosition);
        sphere.add(sprite);
        // In PCAP mode, keep a faint colored sphere so nodes match the
        // list color feel; in other modes keep the legacy icon-only look.
        if (dataSource === PCAP) {
          // For PCAP nodes with user-provided icons, hide the colored sphere.
          // For other PCAP nodes, keep the faint colored sphere so nodes retain identity.
          sphereMaterial.opacity = isPcapIcon ? 0.0 : 0.5;
          sphereMaterial.transparent = true;
        } else {
          sphereMaterial.opacity = 0.0;
          sphereMaterial.transparent = true;
        }
        return sprite; // So we can remove it later
      };
    
      // Handle base64 SVG
      // In PCAP mode, we want the shaded 3D spheres to carry the color/identity.
      // PCAP icons are typically dot sprites (often blue) that would override
      // the sphere color, so we skip sprite rendering entirely.
      // Exception: allow user-provided nametable icons (served under /icons/).
      if (dataSource !== PCAP && node.icon && node.icon.startsWith('data:image/svg+xml;base64,')) {
        const decodedIcon = atob(node.icon.split(',')[1]);
        const blob = new Blob([decodedIcon], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const spriteMap = new TextureLoader().load(url, () => URL.revokeObjectURL(url));
        const spriteMaterial = new SpriteMaterial({
          map: spriteMap,
          alphaMap,
          transparent: true,
          depthTest: false
        });
        const sprite = new Sprite(spriteMaterial);
        sprite.scale.set(size * 2, size * 2, 1);
        sprite.position.set(0, 0, 0);
        sphere.add(sprite);
        if (dataSource === PCAP) {
          sphereMaterial.opacity = 0.35;
          sphereMaterial.transparent = true;
        } else {
          sphereMaterial.opacity = 0.0;
          sphereMaterial.transparent = true;
        }
      }

      else if ((dataSource !== PCAP && node.icon) || isPcapIcon) {
        // SVG support: if .svg, load directly via TextureLoader and exit early
        if (node.icon.endsWith('.svg')) {
          const loader = new SVGLoader();
          loader.load(
            node.icon,
            (data) => {
              const paths = data.paths;
              const group = new Group();

              paths.forEach((path) => {
                const material = new MeshBasicMaterial({
                  color: path.color,
                  side: 2, // DoubleSide
                  depthTest: false,
                  depthWrite: false
                });

                const shapes = SVGLoader.createShapes(path);
                shapes.forEach((shape) => {
                  const geometry = new ShapeGeometry(shape);
                  const mesh = new Mesh(geometry, material);
                  group.add(mesh);
                });
              });

              group.scale.set(size / 100, size / 100, 1);
              group.position.set(0, 0, showText ? 0 : size + 0.1);

              sphere.add(group);
              if (dataSource === PCAP) {
                sphereMaterial.opacity = 0.35;
                sphereMaterial.transparent = true;
              } else {
                sphereMaterial.opacity = 0.0;
                sphereMaterial.transparent = true;
              }
            },
            undefined,
            (err) => {
              console.error(`SVG parse error for ${node.id}`, err);

              // fallback to PNG
              const pngUrl = node.icon.replace('.svg', '.png');
              new TextureLoader().load(
                pngUrl,
                (texture) => {
                  const spriteMaterial = new SpriteMaterial({
                    map: texture,
                    alphaMap,
                    transparent: true,
                    depthTest: false,
                    depthWrite: false
                  });

                  const sprite = new Sprite(spriteMaterial);
                  sprite.scale.set(size * 2, size * 2, 1);
                  sprite.position.set(0, 0, showText ? 0 : size + 0.1);

                  sphere.add(sprite);
                  sphereMaterial.opacity = 0.0;
                  sphereMaterial.transparent = true;
                },
                undefined,
                (fallbackErr) => {
                  console.error(`PNG fallback also failed for ${node.id}`, fallbackErr);
                }
              );
            }
          );

          return;
        }

        if (dataSource === PCAP) {
          loadAsImage();
        } else {
          const imageSprite = loadAsImage(); // Always load image first

          const tryUpgradeToVideo = async () => {
            let isLikelyVideo = false;
            // get the string after the last '/'
            const fileName = node.icon.split('/').pop();
            if (fileName && !fileName.includes('.')) {
              try {
                isLikelyVideo = await isVideo(node.icon);
              } catch (e) {
                console.error(`Error checking MIME type for node ${node.id}`, e);
              }
            }

            if (node.icon.endsWith('.mp4') || isLikelyVideo) {
              try {
                const video = document.createElement('video');
                video.crossOrigin = 'anonymous';
                video.loop = true;
                video.muted = true;
                video.playsInline = true;

                const source = document.createElement('source');
                source.src = node.icon;
                source.type = 'video/mp4';
                video.appendChild(source);

                video.onerror = () => {
                  console.error(`Video load error for ${node.id}`, video.error?.message);
                };

                video.oncanplay = () => {
                  try {
                    const videoTexture = new VideoTexture(video);
                    videoTexture.minFilter = LinearFilter;
                    videoTexture.magFilter = LinearFilter;

                    const spriteMaterial = new SpriteMaterial({
                      map: videoTexture,
                      transparent: true,
                      depthTest: false,
                      depthWrite: false
                    });

                    const sprite = new Sprite(spriteMaterial);
                    sprite.scale.set(size * 2, size * 2, 1);
                    sprite.position.set(0, 0, showText ? 0 : size + 0.1);

                    sphere.remove(imageSprite); // Remove the placeholder
                    sphere.add(sprite);
                    sphereMaterial.opacity = 0.0;
                    sphereMaterial.transparent = true;

                    video.play().catch(err => {
                      console.error(`Video play failed for ${node.id}`, err);
                    });
                  } catch (err) {
                    console.error(`Video texture error for ${node.id}`, err);
                  }
                };

                video.load();
              } catch (err) {
                console.error(`Setup video error for ${node.id}`, err);
              }
            }
          };

          tryUpgradeToVideo();
        }
      }

      else {
        sphereMaterial.opacity = 1.0;
        sphereMaterial.transparent = false;
      }
    
      const shouldShowLabels = (showText || showAddressLabels) && (dataSource !== PCAP || pcapShowLabels !== false);
      if (shouldShowLabels) {
        const mode = segs[0];
        const isStoriesInline = mode === 'stories';
        const isLinks = mode === 'links' || mode === 'graphs';
        const isLinksOrToken = mode === 'links' || mode === 'graphs' || mode === 'token';
        const isLinksDetail = (mode === 'links' || mode === 'graphs') && segs.length >= 2 && String(segs[1]).startsWith('0x');
        const hasIcon = !!node.icon;

        // In /stories, show labels for all nodes.
        // In /links/<address> detail views, also show labels for all nodes so
        // the focused graph is fully annotated.
        // In root /links and /token views, show labels for nodes with icons.
        // However, when allNamesIdentical is true (single-token search like
        // "World Liberty Financial" where every token name is the same),
        // show labels for *all* nodes so address spheres (blue nodes) are
        // annotated as well.
        const shouldShowLabel =
          isStoriesInline ||
          isLinksDetail ||
          (isLinksOrToken && (hasIcon || allNamesIdentical || showAddressLabels)) ||
          (dataSource === PCAP);

        if (shouldShowLabel) {
          const nodeType = node && node.nodeType ? String(node.nodeType) : '';
          const hasIcon = !!(node && node.icon);
          const isTokenNode = nodeType === 'token' || hasIcon;

          let labelText = '';
          if (isTokenNode) {
            if (!showText) return sphere;
            labelText = (!showNames ? node.id : (node.label || node.id));
          } else {
            if (!showAddressLabels) return sphere;
            labelText = String(node && node.id ? node.id : '');
          }

          if (
            labelText &&
            typeof labelText === 'string' &&
            labelText.startsWith('0x') &&
            labelText.length > 14
          ) {
            labelText = formatShortAddress(labelText);
          }

          if (
            // Only apply the "all names identical" address substitution for
            // the main /links and /token galaxy views. In the /stories inline
            // forensics graph we *do* want the primary token's human-friendly
            // name (e.g. "DOGE KILLER") to show instead of its address.
            !isStoriesInline &&
            allNamesIdentical &&
            labelText &&
            typeof labelText === 'string' &&
            !labelText.startsWith('0x') &&
            node.id
          ) {
            // Mirror the NodeList behavior: when every name is the same token
            // (e.g. "Ethereum"), show the address instead so labels
            // differentiate nodes.
            labelText = formatShortAddress(String(node.id));
          }

          if (labelText) {
            const spriteText = new SpriteText(labelText, {
              color: '#FFFFFF',
              // Use null so three-spritetext does not draw any background
              // quad at all, instead of an alpha-blended black rect.
              backgroundColor: 'rgba(0,0,0,0)',
              padding: 0,
              borderWidth: 0,
              borderColor: 'rgba(0,0,0,0)',
            });
            if (spriteText.material) {
              spriteText.material.transparent = true;
              // Ensure labels never occlude links/spheres in depth buffer.
              spriteText.material.depthTest = false;
              spriteText.material.depthWrite = false;
            }

            // Keep labels small for /stories and /links so the graph can
            // expand naturally without becoming a wall of text.
            if (dataSource === PCAP) {
              spriteText.textHeight = Math.max(2, Number(pcapLabelSize) || 0);
            } else {
              const n = Number(labelSize);
              if (!Number.isNaN(n) && Number.isFinite(n) && n > 0) {
                spriteText.textHeight = n;
              } else {
                spriteText.textHeight = (isStoriesInline || isLinks) ? 3.0 : 50.0;
              }
            }
            spriteText.color = '#ffffff';
            spriteText.backgroundColor = 'rgba(0,0,0,0)';
            if (filterNFT) size /= 2;
            spriteText.position.set(size / 2, size, size + 0.1);
            sphere.add(spriteText);
          }
        }
      }
    
      // Story overlay ring: add a colored torus around nodes that have a story.
      const overlayStory = getOverlayStory(node);
      if (overlayStory) {
        const ringColor = storyTypeColorHex(overlayStory.story_type);
        const torusRadius = size * 1.35;
        const tubeRadius  = size * 0.14;
        const torus = new Mesh(
          new TorusGeometry(torusRadius, tubeRadius, 8, 40),
          new MeshBasicMaterial({ color: new Color(ringColor), depthTest: false, depthWrite: false })
        );
        torus.renderOrder = 999;
        sphere.add(torus);
        // Slightly larger glow ring
        const glowTorus = new Mesh(
          new TorusGeometry(torusRadius * 1.22, tubeRadius * 1.5, 6, 40),
          new MeshBasicMaterial({ color: new Color(ringColor), transparent: true, opacity: 0.35, depthTest: false, depthWrite: false })
        );
        glowTorus.renderOrder = 998;
        sphere.add(glowTorus);
      }

      // In /links mode, add a gold star indicator for watchlisted nodes.
      if (watchedKeys && watchedKeys.size > 0) {
        const nodeAddr = String(node.id || '').toLowerCase();
        let isWatchedNode = false;
        watchedKeys.forEach(key => {
          if (!isWatchedNode) {
            const addr = key.split(':')[1];
            if (addr && addr === nodeAddr) isWatchedNode = true;
          }
        });
        if (isWatchedNode) {
          const starSprite = new SpriteText('★');
          starSprite.color = '#ffd700';
          starSprite.backgroundColor = 'rgba(0,0,0,0)';
          starSprite.textHeight = (isStoriesInline || isLinksView) ? 4 : 16;
          if (starSprite.material) {
            starSprite.material.transparent = true;
            starSprite.material.depthTest = false;
            starSprite.material.depthWrite = false;
          }
          starSprite.position.set(size / 2, size + 2, size + 0.1);
          sphere.add(starSprite);
        }
      }

      return sphere;
    },
    
      threeObject: (graphScene) => {
        const light1 = new DirectionalLight(0xffffff, 1);
        light1.position.set(2, 2, 2).normalize();
        graphScene.add(light1);
      
        const light2 = new DirectionalLight(0xffffff, 1);
        light2.position.set(-2, -2, -2).normalize(); // Light from the opposite direction
        graphScene.add(light2);
      
        const ambientLight = new AmbientLight(0x808080);
        graphScene.add(ambientLight);
      
        return graphScene;
      },
    };
  }

  // Keep track of loaded images to avoid reloading and remember failures so we
  // never re-request broken URLs on subsequent renders.
  const imageCache = imageCacheRef && imageCacheRef.current ? imageCacheRef.current : {};
  const failedImageUrls = failedImageUrlsRef && failedImageUrlsRef.current ? failedImageUrlsRef.current : new Set();
  
  return {
    ...commonProps,
    onBackgroundClick: (...args) => {
      if (stopCameraSpin) {
        stopCameraSpin();
      }
      if (handleBackgroundClick) {
        handleBackgroundClick(...args);
      }
    },
    onNodeDragEnd: (...args) => {
      // Resume simulation after drag in 2D mode.
      if (graphRef?.current && graphRef.current.resumeAnimation) {
        try {
          graphRef.current.resumeAnimation();
        } catch (e) {
          console.warn('resumeAnimation error on drag end (2D):', e);
        }
      }

      if (stopCameraSpin) {
        stopCameraSpin();
      }
      if (handleNodeDragEnd) {
        handleNodeDragEnd(...args);
      }
    },
    onNodeDrag: (...args) => {
      // Pause simulation while dragging in 2D so only the dragged node moves.
      if (graphRef?.current && graphRef.current.pauseAnimation) {
        try {
          graphRef.current.pauseAnimation();
        } catch (e) {
          console.warn('pauseAnimation error during drag (2D):', e);
        }
      }

      if (stopCameraSpin) {
        stopCameraSpin();
      }
      if (handleNodeDrag) {
        handleNodeDrag(...args);
      }
    },
    onClick: (...args) => {
      if (stopCameraSpin) {
        stopCameraSpin();
      }
      if (handleClick) {
        handleClick(...args);
      }
    },
    onNodeRightClick: (node, event) => {
      if (handleNodeRightClick) {
        handleNodeRightClick(node, event);
      }
    },
    nodeCanvasObject: (node, ctx, globalScale) => {
      // Special rendering for synthetic chain label nodes: draw a large
      // semi-transparent disk with big centered chain text so each sphere's
      // chain is clearly identified.
      if (showChainLabels && node.nodeType === 'chain_label') {
        const baseRadius = (node.labelRadius || 120) * 0.9;
        const radius = baseRadius / Math.sqrt(globalScale);
        const colorHex = '#' + node.color.toString(16).padStart(6, '0');

        // Semi-transparent colored disk
        ctx.save();
        ctx.globalAlpha = 0.25;
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = colorHex;
        ctx.fill();
        ctx.restore();

        // Big chain name text
        const label = node.label || node.chain || '';
        const fontSize = 28 / Math.sqrt(globalScale);
        ctx.font = `${fontSize}px Sans-Serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.strokeStyle = 'rgba(0,0,0,0.7)';
        ctx.lineWidth = 2;

        ctx.strokeText(label, node.x, node.y);
        ctx.fillText(label, node.x, node.y);
        return; // Do not draw a normal node for labels
      }

      // Calculate node size - larger for nodes with icons and for higher
      // degree (more links). We cap the extra radius contributed by
      // linkCount at 20 so very connected nodes don't dominate.
      const rawBaseRadius = node.icon ? 25 : 6;
      const degree = node.linkCount || 0;
      const extraRadius = Math.min(degree, 20);
      const baseRadius = rawBaseRadius + extraRadius;
      const nodeRadius = Math.max(baseRadius / Math.sqrt(globalScale), 5); // Better scaling for icons
      const labelPadding = 2;
      let colorHex = '#1e90ff';
      try {
        if (node && typeof node.color === 'number' && Number.isFinite(node.color)) {
          colorHex = '#' + node.color.toString(16).padStart(6, '0');
        } else if (node && typeof node.color === 'string' && String(node.color).trim()) {
          colorHex = String(node.color).trim();
        }
      } catch (e) {
        colorHex = '#1e90ff';
      }

      // Story overlay annotation: draw a pulsing ring behind the node.
      if (storyOverlayLookup) {
        const s = getOverlayStory(node);
        if (s) {
          const px = 1 / Math.max(0.0001, globalScale);
          const ringColor = storyTypeColorHex(s && s.story_type);
          // Outer glow ring
          ctx.save();
          ctx.beginPath();
          ctx.arc(node.x, node.y, nodeRadius * 1.35, 0, 2 * Math.PI);
          ctx.strokeStyle = ringColor;
          ctx.lineWidth = px * 4;
          ctx.globalAlpha = 0.4;
          ctx.stroke();
          ctx.restore();
          // Inner solid ring
          ctx.save();
          ctx.beginPath();
          ctx.arc(node.x, node.y, nodeRadius * 1.18, 0, 2 * Math.PI);
          ctx.strokeStyle = ringColor;
          ctx.lineWidth = px * 3;
          ctx.globalAlpha = 1.0;
          ctx.stroke();
          ctx.restore();
        }
      }

      // If node has an icon, draw it
      if (node.icon) {
        const iconSrc = String(node.icon || '').trim();
        const iconFailed = iconSrc && failedImageUrls.has(iconSrc);

        // Cache the image — no crossOrigin needed; we only drawImage() for display,
        // never call getImageData(), so a tainted canvas is not an issue.
        if (iconSrc && !iconFailed && !imageCache[iconSrc]) {
          const img = new Image();
          img.onload = () => {
            try {
              failedImageUrls.delete(iconSrc);
            } catch (e) {
            }
          };
          img.onerror = () => {
            try {
              failedImageUrls.add(iconSrc);
            } catch (e) {
            }
            try {
              delete imageCache[iconSrc];
            } catch (e) {
            }
          };
          imageCache[iconSrc] = img;
          img.src = iconSrc;
        }

        const img = iconSrc ? imageCache[iconSrc] : null;
        
        // Draw the image if it's loaded
        if (!iconFailed && img && img.complete && img.naturalHeight !== 0) {
          const size = nodeRadius * 2;
          
          // Save context state
          ctx.save();
          
          // Create circular clipping path
          ctx.beginPath();
          ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI);
          ctx.closePath();
          
          // Optional: add a subtle border/background
          ctx.fillStyle = 'rgba(255,255,255,0.1)';
          ctx.fill();
          
          // Clip to circle
          ctx.clip();
          
          // Draw the image
          try {
            ctx.drawImage(img, node.x - nodeRadius, node.y - nodeRadius, size, size);
          } catch (e) {
            // If image fails, fall back to colored circle
            ctx.fillStyle = colorHex;
            ctx.fill();
          }
          
          // Restore context state
          ctx.restore();
        } else {
          // While loading, show a colored circle
          ctx.beginPath();
          ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
          ctx.fillStyle = colorHex;
          ctx.fill();
          
          // Image will be drawn on next render cycle
          // The graph automatically updates, no need to manually refresh
        }
      } else {
        // No icon - draw simple colored circle
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
        ctx.fillStyle = colorHex;
        ctx.fill();
      }
  
      // Draw text label
      if ((showText || showAddressLabels) && (dataSource !== PCAP || pcapShowLabels !== false)) {
          const nodeType = node && node.nodeType ? String(node.nodeType) : '';
          const hasIcon = !!(node && node.icon);
          const isTokenNode = nodeType === 'token' || hasIcon;

          let label = '';
          if (isTokenNode) {
            if (!showText) return;
            label = (!showNames ? (node.id || '') : (node.label || node.name || node.id || ''));
          } else {
            if (!showAddressLabels) return;
            label = String(node && node.id ? node.id : '');
          }
          const fontSize = 12/globalScale;
          ctx.font = `${fontSize}px Sans-Serif`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          
          // White text with black shadow for readability
          ctx.shadowColor = 'black';
          ctx.shadowBlur = 3;
          ctx.fillStyle = 'white';
  
          const textX = node.x + nodeRadius + labelPadding;
          ctx.fillText(label, textX, node.y);
          
          // Reset shadow
          ctx.shadowBlur = 0;
      }
    },
  };
};

/**
 * A wrapper component for rendering the ForceGraph in either 2D or 3D mode.
 * @param {Object} props - Component props including mode, data, and event handlers.
 * @returns {JSX.Element} - The rendered ForceGraph component.
 */
export const GraphComponent = ({
  isMobile,
  is3DMode,
  graphData,
  hiddenNodeIds,
  graphRef,
  handleBackgroundClick,
  handleNodeDragEnd,
  handleNodeDrag,
  handleEngineStop,
  handleZoom,
  handleClick,
  handleNodeRightClick,
  handleNodeDoubleClick,
  dataSource,
  filterNFT,
  maxValue,
  imageSize,
  stopCameraSpin,
  startCameraSpin,
  setActiveLinkId,
  handleNodeClick,
  showText,
  showAddressLabels,
  showNames,
  showChainLabels,
  width,
  height,
  currentView,
  spinOnMount,
  pcapLinkStyle,
  pcapShowLabels,
  pcapLabelSize,
  pcapGroupEnabled,
  labelSize,
  pcapSelectedLinkKeys,
  linksDataMode,
  storyNodeMap,
  onStoryNodeClick,
  watchedKeys,
  storyOverlayEnabled,
  storyOverlayMap,
}) => {
  const prevPcapGroupEnabledRef = React.useRef(!!pcapGroupEnabled);
  const imageCacheRef = React.useRef({});
  const failedImageUrlsRef = React.useRef(new Set());

  const graphDataForRender = React.useMemo(() => {
    if (dataSource !== PCAP || !pcapGroupEnabled) return graphData;
    const nodes = graphData && Array.isArray(graphData.nodes) ? graphData.nodes : [];
    const links = graphData && Array.isArray(graphData.links) ? graphData.links : [];
    // In PCAP grouped mode, we want Expand/Compress (and other layout tools)
    // to operate on the current node positions. ForceGraph assigns x/y/z on
    // the node objects it receives. Passing through the original node objects
    // allows app-state nodes to gain coordinates, while we still clone links
    // to prevent ForceGraph from mutating React state link endpoints.
    const outNodes = nodes;
    const outLinks = links.map((l) => {
      if (!l || typeof l !== 'object') return l;
      const s = typeof l.source === 'object' ? l.source.id : l.source;
      const t = typeof l.target === 'object' ? l.target.id : l.target;
      return { ...l, source: s, target: t };
    });
    return { nodes: outNodes, links: outLinks };
  }, [dataSource, pcapGroupEnabled, graphData]);

  React.useEffect(() => {
    if (dataSource !== PCAP || !pcapGroupEnabled) return;
    const id = setTimeout(() => {
      try {
        const nodes = graphDataForRender && Array.isArray(graphDataForRender.nodes) ? graphDataForRender.nodes : [];
        const links = graphDataForRender && Array.isArray(graphDataForRender.links) ? graphDataForRender.links : [];
        if (!nodes.length || !links.length) return;

        let linkEndpointKinds = null;
        try {
          let sObj = 0;
          let tObj = 0;
          for (let i = 0; i < links.length; i += 1) {
            const l = links[i];
            if (!l) continue;
            if (typeof l.source === 'object') sObj += 1;
            if (typeof l.target === 'object') tObj += 1;
          }
          linkEndpointKinds = { sObj, tObj, total: links.length };
        } catch (e) {
        }

        const nodeById = new Map(nodes.map((n) => [String(n && n.id), n]));
        const degree = new Map();
        let missing = 0;
        let noCoords = 0;
        let endpointRefMismatch = 0;
        const samples = [];

        for (let i = 0; i < links.length; i += 1) {
          const l = links[i];
          if (!l) continue;
          const s = typeof l.source === 'object' ? l.source.id : l.source;
          const t = typeof l.target === 'object' ? l.target.id : l.target;
          if (s != null) degree.set(String(s), (degree.get(String(s)) || 0) + 1);
          if (t != null) degree.set(String(t), (degree.get(String(t)) || 0) + 1);

          const sNode = nodeById.get(String(s));
          const tNode = nodeById.get(String(t));
          if (!sNode || !tNode) {
            missing += 1;
            if (samples.length < 5) samples.push({ id: l.id, source: s, target: t, reason: 'missing_node' });
            continue;
          }

          const sx = Number(sNode.x);
          const tx = Number(tNode.x);
          if (!Number.isFinite(sx) || !Number.isFinite(tx)) {
            noCoords += 1;
            if (samples.length < 5) samples.push({ id: l.id, source: s, target: t, reason: 'no_coords' });
          }

          // If link endpoints are objects but not the *same* objects as graphData.nodes,
          // links will not follow dragged/simulated nodes and will appear as a fixed clump.
          const sObj = typeof l.source === 'object' && l.source;
          const tObj = typeof l.target === 'object' && l.target;
          if (sObj) {
            const expected = nodeById.get(String(s));
            if (expected && expected !== l.source) endpointRefMismatch += 1;
          }
          if (tObj) {
            const expected = nodeById.get(String(t));
            if (expected && expected !== l.target) endpointRefMismatch += 1;
          }
        }

        let maxNodeId = null;
        let maxDeg = 0;
        degree.forEach((d, k) => {
          if (d > maxDeg) {
            maxDeg = d;
            maxNodeId = k;
          }
        });

        const maxNode = maxNodeId ? nodeById.get(String(maxNodeId)) : null;
        const maxPos = maxNode ? { x: maxNode.x, y: maxNode.y, z: maxNode.z } : null;

        // Compare against ForceGraph's internal graphData (what it is actually rendering).
        let internalSummary = null;
        let refInfo = null;
        try {
          const ref = graphRef && graphRef.current;
          refInfo = ref ? {
            hasRef: true,
            hasGraphDataFn: typeof ref.graphData === 'function',
            hasD3Reheat: typeof ref.d3ReheatSimulation === 'function',
            keys: Object.keys(ref).slice(0, 25),
          } : { hasRef: false };

          const gd = ref && typeof ref.graphData === 'function' ? ref.graphData() : null;
          const inNodes = gd && Array.isArray(gd.nodes) ? gd.nodes : null;
          const inLinks = gd && Array.isArray(gd.links) ? gd.links : null;
          if (inNodes && inLinks) {
            const sampleNode = inNodes.find((n) => n && n.id != null) || null;
            const sampleNodePos = sampleNode ? { id: sampleNode.id, x: sampleNode.x, y: sampleNode.y, z: sampleNode.z, fx: sampleNode.fx, fy: sampleNode.fy, fz: sampleNode.fz } : null;
            const internalLink = inLinks.find((l) => l) || null;
            const sObj = internalLink && typeof internalLink.source === 'object';
            const tObj = internalLink && typeof internalLink.target === 'object';
            const sPos = sObj && internalLink.source ? { x: internalLink.source.x, y: internalLink.source.y, z: internalLink.source.z } : null;
            const tPos = tObj && internalLink.target ? { x: internalLink.target.x, y: internalLink.target.y, z: internalLink.target.z } : null;
            internalSummary = {
              nodes: inNodes.length,
              links: inLinks.length,
              sampleNodePos,
              sampleLinkEndpointTypes: { sObj, tObj },
              sampleLinkEndpointPos: { sPos, tPos },
            };
          }
        } catch (e) {
        }

        console.log(
          'PCAP grouped render debug:',
          'links=',
          links.length,
          'missingNodes=',
          missing,
          'noCoords=',
          noCoords,
          'maxDeg=',
          maxDeg,
          'maxNode=',
          maxNodeId,
          'maxPos=',
          maxPos,
          'endpointRefMismatch=',
          endpointRefMismatch,
          'linkEndpointKinds=',
          linkEndpointKinds,
          'refInfo=',
          refInfo,
          'internal=',
          internalSummary,
          samples.length ? samples : ''
        );
      } catch (e) {
      }
    }, 2000);

    return () => clearTimeout(id);
  }, [dataSource, pcapGroupEnabled, graphDataForRender]);

  // Use React.useEffect with empty dependency array to run ONCE on mount only
  React.useEffect(() => {
    // This is a one-time initialization when the component mounts
    // We'll position the camera and start spinning only ONCE
    if (graphRef?.current && graphData?.nodes?.length > 0) {
      console.log('Initial graph positioning');
      
      // Separate paths for 2D and 3D mode
      const isLinksMount = typeof window !== 'undefined' && window.location.pathname.startsWith('/links');
      if (!is3DMode && graphRef.current.zoomToFit && !isLinksMount) {
        // Simple fit for 2D mode — skip on /links so the organic simulation
        // runs freely without the camera snapping to fit mid-simulation.
        console.log('Auto-fitting 2D graph on mount');
        // Short delay to ensure graph is ready
        setTimeout(() => {
          graphRef.current.zoomToFit(400, 10);
        }, 300);
      } 
      else if (is3DMode && spinOnMount) {
        // For 3D, let the normal animation handle it. Only start the initial
        // spin if the caller has requested it; chain mode defers spin until
        // after auto-fit.
        console.log('Initializing 3D graph, ensuring spin is active');
        
        // Ensure spin is on (with short delay to allow graph to initialize)
        setTimeout(() => {
          if (startCameraSpin) {
            startCameraSpin();
          }
        }, 500);
      }
    }
    // Empty dependency array ensures this only runs on mount
  }, []); // ⚠️ Note the empty dependency array - this is important to prevent re-rendering!
 
  // For the inline Stories forensics 3D view, auto-fit the graph whenever
  // the data changes so it uses the available space.
  // NOTE: Do NOT auto-fit for /links — the organic simulation should run freely
  // without the camera snapping to fit mid-simulation (causes zoom-out).
  React.useEffect(() => {
    if (!is3DMode) return;
    if (!graphRef?.current) return;
    if (!graphData || !Array.isArray(graphData.nodes) || !graphData.nodes.length) return;

    // Only auto-fit for the inline /stories forensics view, not for /links.
    if (currentView !== '/stories') return;

    const ref = graphRef.current;
    const id = setTimeout(() => {
      try {
        if (ref.zoomToFit) {
          ref.zoomToFit(650, 40);
        }
      } catch (e) {
        console.warn('zoomToFit error:', e);
      }
    }, 80);

    return () => clearTimeout(id);
  }, [is3DMode, currentView, graphData, graphRef]);

  // PCAP graphs: after switching group/MAC modes we often need to reheat the
  // simulation and fit the camera, but calling into the graph during teardown
  // can crash three-forcegraph (layout.tick undefined). Do it here, after mount,
  // and cancel on unmount.
  React.useEffect(() => {
    if (dataSource !== PCAP) return;
    if (!pcapGroupEnabled) return;
    if (!graphData || !Array.isArray(graphData.links) || !Array.isArray(graphData.nodes)) return;

    const wasGrouped = !!prevPcapGroupEnabledRef.current;
    prevPcapGroupEnabledRef.current = true;

    const ref = graphRef.current;
    let cancelled = false;
    const id = setTimeout(() => {
      if (cancelled) return;
      try {
        if (ref && typeof ref.d3ReheatSimulation === 'function') {
          ref.d3ReheatSimulation();
        }
        if (ref && typeof ref.resumeAnimation === 'function') {
          ref.resumeAnimation();
        }
        if (!wasGrouped && ref && typeof ref.zoomToFit === 'function') {
          // Only auto-fit when entering grouped mode. Avoid overriding user/layout
          // adjustments like Expand/Compress which update graphData.
          const pad = 80;
          ref.zoomToFit(700, pad);
        }
      } catch (e) {
      }
    }, 120);

    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [dataSource, pcapGroupEnabled, graphData, graphRef, is3DMode]);

  React.useEffect(() => {
    // react-force-graph caches node threeObjects. When label toggles or label
    // sizing changes, force a refresh so SpriteText objects are rebuilt to
    // match the current settings.
    try {
      const ref = graphRef && graphRef.current;
      if (!ref) return;
      if (typeof ref.refresh === 'function') {
        ref.refresh();
      } else if (typeof ref.d3ReheatSimulation === 'function') {
        // Fallback: at least trigger a render cycle in older versions.
        ref.d3ReheatSimulation();
      }
    } catch (e) {
    }
  }, [graphRef, is3DMode, dataSource, showText, showAddressLabels, showNames, labelSize, pcapShowLabels, pcapLabelSize, storyNodeMap, watchedKeys, storyOverlayEnabled, storyOverlayMap]);


  React.useEffect(() => {
    // Keep this ref in sync when group mode is turned off.
    if (!pcapGroupEnabled) {
      prevPcapGroupEnabledRef.current = false;
    }
  }, [pcapGroupEnabled]);

  const graphProps = useMemo(
    () =>
      getForceGraphProps(
        isMobile,
        is3DMode,
        graphDataForRender,
        hiddenNodeIds,
        graphRef,
        handleBackgroundClick,
        handleNodeDragEnd,
        handleNodeDrag,
        handleEngineStop,
        handleZoom,
        handleClick,
        handleNodeRightClick,
        handleNodeDoubleClick,
        dataSource,
        filterNFT,
        maxValue,
        imageSize,
        stopCameraSpin,
        setActiveLinkId,
        handleNodeClick,
        showText,
        showAddressLabels,
        showNames,
        showChainLabels,
        currentView,
        pcapGroupEnabled,
        pcapLinkStyle,
        pcapShowLabels,
        pcapLabelSize,
        labelSize,
        pcapSelectedLinkKeys,
        storyNodeMap,
        onStoryNodeClick,
        watchedKeys,
        storyOverlayEnabled,
        storyOverlayMap,
        imageCacheRef,
        failedImageUrlsRef,
      ),
    [
      isMobile,
      is3DMode,
      graphDataForRender,
      hiddenNodeIds,
      graphRef,
      handleBackgroundClick,
      handleNodeDragEnd,
      handleNodeDrag,
      handleEngineStop,
      handleZoom,
      handleClick,
      handleNodeRightClick,
      handleNodeDoubleClick,
      dataSource,
      filterNFT,
      maxValue,
      imageSize,
      stopCameraSpin,
      setActiveLinkId,
      handleNodeClick,
      showText,
      showAddressLabels,
      showNames,
      showChainLabels,
      currentView,
      pcapGroupEnabled,
      pcapLinkStyle,
      pcapShowLabels,
      pcapLabelSize,
      labelSize,
      pcapSelectedLinkKeys,
      storyNodeMap,
      onStoryNodeClick,
      watchedKeys,
      storyOverlayEnabled,
      storyOverlayMap,
      imageCacheRef,
      failedImageUrlsRef,
    ]
  );

  const ForceGraphComponent = is3DMode ? ForceGraph3D : ForceGraph2D;

  const forceGraphKey = [
    is3DMode ? '3d' : '2d',
    showText ? 'token1' : 'token0',
    showAddressLabels ? 'addr1' : 'addr0',
    showNames ? 'names1' : 'names0',
    `ls${labelSize ?? ''}`,
    dataSource,
    pcapShowLabels ? 'pcapLbl1' : 'pcapLbl0',
    `pl${pcapLabelSize ?? ''}`,
  ].join('|');

  const [legendVisible, setLegendVisible] = React.useState(() => {
    try { return !window.localStorage.getItem('e3d_graph_legend_dismissed'); } catch (_) { return true; }
  });

  const graphNodes = graphData && Array.isArray(graphData.nodes) ? graphData.nodes : [];
  const graphLinks = graphData && Array.isArray(graphData.links) ? graphData.links : [];
  const nodeCount = graphNodes.length;
  const linkCount = graphLinks.length;

  const graphSummary = React.useMemo(() => {
    if (nodeCount === 0) return null;
    if (nodeCount === 1 && linkCount === 0) return 'No transaction connections found — price movement has no on-chain evidence.';
    const centralNode = graphNodes.reduce((best, n) => ((n.linkCount || 0) > (best.linkCount || 0) ? n : best), graphNodes[0]);
    const centralLabel = centralNode && (centralNode.label || centralNode.name || centralNode.id) ? (centralNode.label || centralNode.name || String(centralNode.id)).slice(0, 30) : null;
    const reciprocalLinks = graphLinks.filter((l) => {
      const src = l.source && (l.source.id || l.source);
      const tgt = l.target && (l.target.id || l.target);
      return graphLinks.some((l2) => {
        const s2 = l2.source && (l2.source.id || l2.source);
        const t2 = l2.target && (l2.target.id || l2.target);
        return s2 === tgt && t2 === src;
      });
    });
    const highReciprocity = reciprocalLinks.length > linkCount * 0.4 && linkCount > 4;
    if (highReciprocity) return `High reciprocity (${reciprocalLinks.length}/${linkCount} links) — suggests wash trading or circular flows.`;
    if (centralLabel) return `${nodeCount} nodes, ${linkCount} edges. Central flow through ${centralLabel}.`;
    return `${nodeCount} nodes, ${linkCount} edges.`;
  }, [nodeCount, linkCount, graphNodes, graphLinks]);

  const LEGEND_ENTRIES = [
    { color: '#4d79ff', label: 'FUNNEL', types: ['FUNNEL'] },
    { color: '#9ef7c5', label: 'CLUSTER', types: ['CLUSTER'] },
    { color: '#ffe55c', label: 'LOOP', types: ['LOOP'] },
    { color: '#b45aff', label: 'MIRROR', types: ['MIRROR'] },
    { color: '#ffd700', label: 'WHALE', types: ['WHALE'] },
    { color: '#00c2ff', label: 'SURGE / FLOW', types: ['SURGE', 'FLOW'] },
    { color: '#ff6b6b', label: 'HOTLINKS', types: ['HOTLINKS'] },
    { color: '#cfd3ff', label: 'THESIS', types: ['THESIS'] },
    { color: '#ffa500', label: 'SANDWICH', types: ['SANDWICH'] },
  ];

  const storyCounts = React.useMemo(() => {
    const counts = {};
    if (storyOverlayMap && typeof storyOverlayMap.values === 'function') {
      for (const stories of storyOverlayMap.values()) {
        if (!Array.isArray(stories)) continue;
        stories.forEach((s) => {
          const t = String(s && s.story_type ? s.story_type : '').trim().toUpperCase();
          if (t) counts[t] = (counts[t] || 0) + 1;
        });
      }
    }
    return counts;
  }, [storyOverlayMap]);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <ForceGraphComponent
        key={forceGraphKey}
        {...graphProps}
        width={width}
        height={height}
      />
      <div style={{ position: 'absolute', bottom: 36, left: 10, zIndex: 10, maxWidth: 220 }}>
        {(linksDataMode === 'stories' || storyOverlayEnabled) && legendVisible ? (
          <div style={{ background: 'rgba(8,12,28,0.88)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 10px', fontSize: 11, color: '#ccc' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 11, color: '#9ea4d8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Legend</span>
              <span
                style={{ cursor: 'pointer', color: '#666', fontSize: 13, lineHeight: 1 }}
                onClick={() => { setLegendVisible(false); try { window.localStorage.setItem('e3d_graph_legend_dismissed', '1'); } catch (_) {} }}
              >×</span>
            </div>
            {LEGEND_ENTRIES.map((e) => {
              const count = e.types.reduce((sum, t) => sum + (storyCounts[t] || 0), 0);
              return (
                <div key={e.label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, opacity: count > 0 ? 1 : 0.35 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: e.color, flexShrink: 0, boxShadow: count > 0 ? `0 0 4px ${e.color}` : 'none' }} />
                  <span>{e.label}{count > 0 ? <span style={{ color: '#aaa', marginLeft: 3 }}>({count})</span> : null}</span>
                </div>
              );
            })}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 6, paddingTop: 6, color: '#888' }}>
              Node size = connection degree
            </div>
          </div>
        ) : (linksDataMode === 'stories' || storyOverlayEnabled) ? (
          <button
            onClick={() => { setLegendVisible(true); try { window.localStorage.removeItem('e3d_graph_legend_dismissed'); } catch (_) {} }}
            style={{ background: 'rgba(8,12,28,0.75)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: '#9ea4d8', fontSize: 11, padding: '3px 8px', cursor: 'pointer' }}
          >
            Legend ▾
          </button>
        ) : null}
      </div>
      {graphSummary && (
        <div style={{ position: 'absolute', bottom: 8, left: 0, right: 0, textAlign: 'center', pointerEvents: 'none', zIndex: 10 }}>
          <span style={{ background: 'rgba(8,12,28,0.75)', borderRadius: 6, padding: '3px 10px', fontSize: 11, color: '#9ea4d8' }}>
            {graphSummary}
          </span>
        </div>
      )}
    </div>
  );
};
